import type { Participant } from '@seen/core/models/patients/participant'
import {
  ATTENDANCE_WEEKDAYS,
  DayCenterSchedule,
} from '@seen/core/models/schedules/dayCenterSchedule'
import {
  Button,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@seen/ui'
import { type JSX, useEffect } from 'react'
import { useFormState } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { useDayCenterScheduleActivity } from '#src/components/patient/detail-content/day-center-schedule/UseDayCenterScheduleActivity.tsx'
import {
  daysPerWeekLabel,
  WEEKDAY_LETTER_LABELS,
  WEEKDAY_LONG_LABELS,
} from '#src/components/patient/detail-content/day-center-schedule/weekdays.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export interface DayCenterScheduleDialogV1Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  participant: Participant
  schedule: DayCenterSchedule | null
}

// react-hook-form / useForm disallow `undefined` field types, so use nullable
// (not optional) for the note.
const formSchema = z.object({
  days: z.array(z.enum(ATTENDANCE_WEEKDAYS)),
  note: z.string().nullable(),
})

export function DayCenterScheduleDialogV1({
  open,
  onOpenChange,
  participant,
  schedule,
}: DayCenterScheduleDialogV1Props): JSX.Element {
  const isReadOnly = useIsReadOnly()

  // Only fetch version history while the dialog is open.
  const activity = useDayCenterScheduleActivity(open ? schedule?.id : undefined)

  const scheduleDays = schedule?.attendanceDays ?? []
  const scheduleDaysKey = scheduleDays.join('|')
  const scheduleNote = schedule?.note ?? ''

  const form = useForm({
    schema: formSchema,
    defaultValues: { days: scheduleDays, note: scheduleNote },
  })
  const { isDirty } = useFormState({ control: form.control })
  const { reset } = form

  // Reset only on dialog open or schedule identity changes; background refetches
  // for the same schedule must not discard in-progress edits.
  useEffect(() => {
    if (open) {
      const selectedDays = new Set(scheduleDaysKey.split('|'))
      reset({
        days: ATTENDANCE_WEEKDAYS.filter((day) => selectedDays.has(day)),
        note: scheduleNote,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- content-derived values intentionally do not trigger resets
  }, [open, reset, schedule?.id])

  const patchMutation = usePatchMutation<DayCenterSchedule>()
  const createMutation = useCreateMutation<DayCenterSchedule>({
    disableDefaultErrorToast: true,
  })
  const isSaving = patchMutation.isPending || createMutation.isPending

  const submit = form.handleSubmit((values) => {
    const onSuccess = (): void => {
      onOpenChange(false)
      toast.success('Day center schedule updated')
    }
    const onError = (cause: unknown): void => {
      toast.error(
        cause instanceof Error ? cause.message : 'Failed to update schedule'
      )
    }

    if (schedule) {
      patchMutation.mutate(
        {
          instance: schedule,
          mutateFn: (next) => {
            next.attendanceDays = values.days
            next.note = values.note ?? undefined
          },
        },
        { onSuccess, onError }
      )
      return
    }

    const created = new DayCenterSchedule({
      participant: participant.reference,
    })
    created.attendanceDays = values.days
    created.note = values.note ?? undefined
    createMutation.mutate(created, { onSuccess, onError })
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader className="flex-col gap-1">
          <div className="flex items-center gap-2">
            <DialogTitle>Day center schedule</DialogTitle>
            {schedule && <DevMedplumAppLinkOverlay to={schedule} />}
          </div>
          <DialogDescription>Recurring weekly attendance</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="schedule">
          <div className="px-6">
            <TabsList variant="underline" className="w-full justify-start">
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
              <TabsTrigger value="history">
                History{activity.length > 0 && ` · ${activity.length}`}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="schedule">
            <Form onSubmit={submit}>
              <DialogContent className="gap-5">
                <FormField
                  control={form.control}
                  name="days"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <Label>Days attending</Label>
                        <span className="text-sm text-muted-foreground">
                          {daysPerWeekLabel(value.length)}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {ATTENDANCE_WEEKDAYS.map((day) => {
                          const selected = value.includes(day)
                          return (
                            <Tooltip key={day}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant={selected ? 'default' : 'outline'}
                                  disabled={isReadOnly}
                                  aria-pressed={selected}
                                  aria-label={WEEKDAY_LONG_LABELS[day]}
                                  className="rounded-full"
                                  onClick={() => {
                                    const next = selected
                                      ? value.filter((d) => d !== day)
                                      : [...value, day]
                                    onChange(
                                      ATTENDANCE_WEEKDAYS.filter((d) =>
                                        next.includes(d)
                                      )
                                    )
                                  }}
                                >
                                  {WEEKDAY_LETTER_LABELS[day]}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {WEEKDAY_LONG_LABELS[day]}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </div>
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name="note"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label>Notes & scheduling preferences</Label>
                      <Textarea
                        disabled={isReadOnly}
                        placeholder="Specialists Mon/Wed/Fri only; no Wednesday afternoons; transportation arranged; family request..."
                        className="placeholder:text-foreground/50"
                        value={value ?? ''}
                        onChange={onChange}
                        {...rest}
                      />
                      <span className="text-xs text-muted-foreground">
                        Saved on the schedule and used to guide AI appointment
                        suggestions.
                      </span>
                    </div>
                  )}
                />
              </DialogContent>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </DialogClose>
                <SubmitButton
                  submitting={isSaving}
                  disabled={isReadOnly || !isDirty}
                >
                  Save changes
                </SubmitButton>
              </DialogFooter>
            </Form>
          </TabsContent>
          <TabsContent value="history">
            <DialogContent>
              {activity.length > 0 ? (
                <ActivityList>{activity}</ActivityList>
              ) : (
                <span className="text-sm text-muted-foreground">
                  No changes recorded yet.
                </span>
              )}
            </DialogContent>
          </TabsContent>
        </Tabs>
      </DialogDisplay>
    </Dialog>
  )
}
